//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import Button from '@components/atoms/button/Button';
import {
  DefaultButtonStyles,
  DefaultErrorButtonStyles,
  DefaultErrorPlaceholderButtonStyles,
  DefaultPlaceholderButtonStyles,
} from '@components/atoms/button/buttonStyles';

type ButtonProps = Omit<React.ComponentProps<typeof Button>, 'buttonStyles'>;

interface Props extends ButtonProps {
  valueText?: string | React.ReactNode;
  placeholderText?: string;
  hasError?: boolean;
}

/**
 * The DropdownButton atom renders a default Button with a chevron icon,
 * and the given value text. It renders the given placeholder text if no
 * value text is given.
 *
 * It should be used as the Popover target for components
 * like pickers and selects.
 */
const DropdownButton = forwardRef<
  HTMLButtonElement,
  Props
>((
  {
    className,
    placeholderText,
    valueText,
    hasError = false,
    isDisabled = false,
    ...buttonProps
  }: Props,
  ref
) => {
  const buttonStyles = (() => {
    if (hasError) {
      return valueText
        ? DefaultErrorButtonStyles
        : DefaultErrorPlaceholderButtonStyles;
    } else {
      return valueText ? DefaultButtonStyles : DefaultPlaceholderButtonStyles;
    }
  })();

  return (
    <CustomButton
      buttonStyles={buttonStyles}
      className={className}
      hasError={hasError}
      isDisabled={isDisabled}
      ref={ref}
      {...buttonProps}
    >
      {valueText ?? placeholderText}
    </CustomButton>
  );
});

// Add a box-shadow here so that it appears even when the button is disabled
const CustomButton = styled(Button)<{ hasError: boolean; isDisabled: boolean }>(
  ({ theme, hasError, isDisabled }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    ${(!hasError || isDisabled) &&
    `box-shadow: inset 0 0 0 1px ${theme.colors.gray2}`};
  `,
);

DropdownButton.displayName = 'DropdownButton';
export default DropdownButton;
